/**
 * packages/registry — THE keystone (ADR-005, CONSTRAINTS #5).
 *
 * This module exports the assembled registry: the ONLY place collection keys
 * exist. Every consumer (API, Sheets, UI, chat tools, scoping, i18n) derives
 * from it by in-process import. To add a field or collection:
 * docs/recipes/add-a-collection.md.
 */
import { fieldsOf,  assembleRegistry } from "./define.ts";
import { schedule } from "./collections/schedule.ts";
import { hotels } from "./collections/hotels.ts";
import { contacts } from "./collections/contacts.ts";
import { travel } from "./collections/travel.ts";
import { timetable } from "./collections/timetable.ts";
import { crew } from "./collections/crew.ts";
import { guests } from "./collections/guests.ts";
import { setlist } from "./collections/setlist.ts";
import { rider } from "./collections/rider.ts";
import { perdiem } from "./collections/perdiem.ts";
import { files } from "./collections/files.ts";
import { links } from "./collections/links.ts";
import { notes_list } from "./collections/notes_list.ts";
import { events } from "./collections/events.ts";
import { promo } from "./collections/promo.ts";
import { marketing } from "./collections/marketing.ts";

export * from "./define.ts";
export * from "./version.ts";

// P3: the FULL registry — v1 feature parity. 16 collections = v1's 17 minus
// flights+transport, which merge into `travel` (one row per person, departure
// AND arrival date+time, layovers as extra legs — ARCHITECTURE §3.2). The gig
// meta/header field spec (required IANA `timezone` etc., §12) still ships with
// the P3 gig-entity work; until then `gig_id` on Ops is owned by sync-core.
export const registry = assembleRegistry([
  schedule,
  timetable,
  travel,
  hotels,
  crew,
  contacts,
  guests,
  setlist,
  rider,
  perdiem,
  files,
  links,
  notes_list,
  events,
  promo,
  marketing,
]);

export type Registry = typeof registry;
export type CollectionKey = keyof Registry;

/** All collection keys, declaration order. The one legal "list of collections". */
export const collectionKeys = Object.freeze(Object.keys(registry)) as readonly CollectionKey[];

export function isCollectionKey(s: string): s is CollectionKey {
  return Object.prototype.hasOwnProperty.call(registry, s);
}

export function getCollection(key: CollectionKey | string): Registry[CollectionKey] {
  if (!isCollectionKey(key)) throw new Error(`unknown collection: ${JSON.stringify(key)}`);
  return registry[key];
}

// wire/legacy name -> canonical field key, per collection (renamed_from lineage,
// CONSTRAINTS #6: old clients' queued ops must migrate losslessly or park loudly).
const wireIndex: Readonly<Record<CollectionKey, ReadonlyMap<string, string>>> = Object.freeze(
  Object.fromEntries(
    collectionKeys.map((ckey) => {
      const m = new Map<string, string>();
      for (const f of Object.values(registry[ckey].fields)) {
        m.set(f.key, f.key);
        m.set(f.wireName, f.key);
        for (const legacy of f.renamedFrom) m.set(legacy, f.key);
      }
      return [ckey, m];
    }),
  ) as unknown as Record<CollectionKey, ReadonlyMap<string, string>>,
);

/**
 * Resolve a wire name, legacy (renamed_from) name, or canonical key to the
 * canonical field key. Returns null for unknown names (strict-validate-or-park:
 * the caller parks the op, it never silently drops the field — CONSTRAINTS #3).
 */
export function resolveFieldKey(collection: CollectionKey | string, name: string): string | null {
  if (!isCollectionKey(collection)) throw new Error(`unknown collection: ${JSON.stringify(collection)}`);
  return wireIndex[collection].get(name) ?? null;
}

/**
 * Content-level dedup key for an op (ARCHITECTURE §4.1: `dedup_key` is derived
 * from registry dedupFields). Returns null when the collection declares no
 * dedupFields or any of them is absent from the given fields — dedup only
 * applies when the identifying content is fully present.
 */
export function dedupKeyFor(
  collection: CollectionKey | string,
  fields: Readonly<Record<string, unknown>>,
): string | null {
  const def = getCollection(collection);
  if (def.dedupFields.length === 0) return null;
  const parts: string[] = [];
  for (const fkey of def.dedupFields) {
    const wireName = fieldsOf(def)[fkey]!.wireName;
    const v = fields[wireName] ?? fields[fkey];
    if (v === undefined || v === null || v === "") return null;
    parts.push(`${wireName}=${encodeURIComponent(String(v))}`);
  }
  return `${def.key}:${parts.join("|")}`;
}
